import DashboardGrid from '@/components/dashboard/dashboard-grid/dashboard-grid';
import useDashboardBodyController from './dashboard-body.controller';
import DashboardLayout from '@/components/dashboard/dashboard-layout/dashboard-layout';
import ComponentActionItem from './components/component-action-item-card';
import Top3Risks from './components/top-3-risks/top-3-risks';
import TestingPlan from './components/testing-plan/testing-plan';
import RecommendedActions from './components/recommended-actions/recommended-actions';
import RiskFactors from './components/risk-factors/risk-factors';
import { BiomarkerSummaryCard } from './components/biomarker-summary-card/biomarker-summary-card';
import { CriticalLabNotificationBanner } from '../../../notifications';
import { LabOrderListResponse } from '@/api/generated-catch-backend/model';
import { CatchLabsCTA } from '@/app/components/common/CatchLabsCTA';
import { LlmDoctorsNoteCallout } from './components/llm-doctors-note-callout';
import { useDoctorsNoteFeatures } from '@/services/feature-flag-provider';

export default function DashboardBody({
  children,
  labOrdersData,
  isEligibleForLabOrder,
}: {
  children: React.ReactNode;
  labOrdersData?: LabOrderListResponse;
  isEligibleForLabOrder?: boolean;
}) {
  const {
    riskScoresData,
    testSuggestionData,
    recommendedActionsData,
    hasCompleteLabResults,
    biomarkerCounts,
    notifications,
    isDismissingNotification,
    handleViewResults,
    handleDismissNotification,
  } = useDashboardBodyController(labOrdersData, isEligibleForLabOrder);

  // Feature flags for doctors note - ensures mutual exclusivity between legacy and LLM versions
  const { isLlmDoctorsNoteEnabled } = useDoctorsNoteFeatures();

  return (
    <>
      {/* Critical Lab Notifications */}
      {notifications.length > 0 && (
        <CriticalLabNotificationBanner
          notification={notifications[0]}
          onViewResults={() => handleViewResults(notifications[0].notificationId)}
          onDismiss={() => handleDismissNotification(notifications[0].notificationId)}
          isLoading={isDismissingNotification}
        />
      )}

      <DashboardLayout>
        <DashboardGrid>
          <div className="dashboard-reset">
            <ComponentActionItem>
              <Top3Risks risks={riskScoresData} />
            </ComponentActionItem>
            {/* LLM Doctors Note Callout - only show when feature flag enabled */}
            {isLlmDoctorsNoteEnabled && (
              <ComponentActionItem>
                <LlmDoctorsNoteCallout />
              </ComponentActionItem>
            )}
            {hasCompleteLabResults && (
              <ComponentActionItem>
                <BiomarkerSummaryCard
                  optimalCount={biomarkerCounts.optimal}
                  normalCount={biomarkerCounts.normal}
                  outOfRangeCount={biomarkerCounts.outOfRange}
                />
              </ComponentActionItem>
            )}
            <ComponentActionItem>
              <RiskFactors />
            </ComponentActionItem>
            {/* CatchLabsCTA: shows as 3rd card on mobile, in sidebar on desktop */}
            <div className="lg:hidden">
              <CatchLabsCTA />
            </div>
            <ComponentActionItem>
              <TestingPlan testSuggestions={testSuggestionData} />
            </ComponentActionItem>
            <ComponentActionItem>
              <RecommendedActions recommendedActions={recommendedActionsData} />
            </ComponentActionItem>
          </div>
          <div className="flex flex-col">
            {children}
          </div>
        </DashboardGrid>
      </DashboardLayout>
    </>
  );
}
